"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildSliderBandSegmentKey = buildSliderBandSegmentKey;
exports.buildSliderTrackGroupKey = buildSliderTrackGroupKey;
exports.buildSliderTrackGroups = buildSliderTrackGroups;
exports.computeAllowedCashSegments = computeAllowedCashSegments;
exports.computeOfferRanges = computeOfferRanges;
exports.computeGroupAprBands = computeGroupAprBands;
exports.clampAndSnapToAllowedSegments = clampAndSnapToAllowedSegments;
exports.resolveActiveSegment = resolveActiveSegment;
exports.pickBestEligibleGroupKey = pickBestEligibleGroupKey;
exports.getSliderGroupDetails = getSliderGroupDetails;
exports.buildTrackConfigFromLegacyConfig = buildTrackConfigFromLegacyConfig;
exports.computeTrackBoundsFromAllowedSegments = computeTrackBoundsFromAllowedSegments;
exports.isBuildSliderTrackConfigFailure = isBuildSliderTrackConfigFailure;
exports.buildSliderTrackConfig = buildSliderTrackConfig;
const configBuilder_1 = require("./configBuilder");
const math_1 = require("./math");
const offerDetails_1 = require("./offerDetails");
const offerFilter_1 = require("./offerFilter");
const sliderBounds_1 = require("./sliderBounds");
const SLIDER_TRACK_GROUP_PARAM_EPSILON = 0.000001;
function buildSliderBandSegmentKey(offerIdentifier, minLineSizeCents) {
    return `${offerIdentifier}:${minLineSizeCents}`;
}
function buildSliderTrackGroupKey(fixedTermMonths, firstDrawFeeDecimal, refiPositions) {
    // Sort a copy so the key is invariant to the caller's position order; refiPositions is
    // produced sorted by getOfferRefiPositions, but the key must not depend on that staying true.
    const refiPositionsKey = refiPositions.length > 0 ? [...refiPositions].sort((a, b) => a - b).join(',') : 'none';
    return `fixedTermMonths=${fixedTermMonths}|firstDrawFeeDecimal=${firstDrawFeeDecimal}|refiPositions=${refiPositionsKey}`;
}
function valuesMatch(left, right) {
    return Math.abs(left - right) <= SLIDER_TRACK_GROUP_PARAM_EPSILON;
}
function getSharedParamMismatch(group, offer) {
    const numericChecks = [
        {
            name: 'firstDrawFeeDecimal',
            groupValue: group.firstDrawFeeDecimal,
            offerValue: offer.firstDrawFeeDecimal,
        },
        {
            name: 'oneTimeFeeDecimal',
            groupValue: group.oneTimeFeeDecimal,
            offerValue: offer.oneTimeFeeDecimal,
        },
        {
            name: 'minDrawDownDecimal',
            groupValue: group.minDrawDownDecimal,
            offerValue: offer.minDrawDownDecimal,
        },
        {
            name: 'recordingTaxFixedCents',
            groupValue: group.recordingTaxFixedCents,
            offerValue: offer.recordingTaxFixedCents,
        },
        {
            name: 'recordingTaxVariableDecimal',
            groupValue: group.recordingTaxVariableDecimal,
            offerValue: offer.recordingTaxVariableDecimal,
        },
        {
            name: 'refiPayoffCents',
            groupValue: group.refiPayoffCents,
            offerValue: offer.refiPayoffCents,
        },
        {
            name: 'priorLienPayoffFee',
            groupValue: group.priorLienPayoffFee,
            offerValue: offer.priorLienPayoffFee,
        },
        {
            name: 'balanceTransferFeePercent',
            groupValue: group.balanceTransferFeePercent,
            offerValue: offer.balanceTransferFeePercent,
        },
        {
            name: 'secondLienBalanceBufferPercent',
            groupValue: group.secondLienBalanceBufferPercent,
            offerValue: offer.secondLienBalanceBufferPercent,
        },
    ];
    for (const check of numericChecks) {
        if (!valuesMatch(check.groupValue, check.offerValue)) {
            return `${check.name} differs within ${group.groupKey}: group=${check.groupValue}, offer=${check.offerValue}`;
        }
    }
    if (group.refiLenderName !== offer.refiLenderName) {
        return `refiLenderName differs within ${group.groupKey}: group=${group.refiLenderName}, offer=${offer.refiLenderName}`;
    }
    return null;
}
function makeGroupFromOffer(offer) {
    return {
        groupKey: buildSliderTrackGroupKey(offer.fixedTermMonths, offer.firstDrawFeeDecimal, offer.refiPositions),
        fixedTermMonths: offer.fixedTermMonths,
        firstDrawFeeDecimal: offer.firstDrawFeeDecimal,
        oneTimeFeeDecimal: offer.oneTimeFeeDecimal,
        minDrawDownDecimal: offer.minDrawDownDecimal,
        recordingTaxFixedCents: offer.recordingTaxFixedCents,
        recordingTaxVariableDecimal: offer.recordingTaxVariableDecimal,
        refiPayoffCents: offer.refiPayoffCents,
        refiPositions: offer.refiPositions,
        priorLienPayoffFee: offer.priorLienPayoffFee,
        balanceTransferFeePercent: offer.balanceTransferFeePercent,
        secondLienBalanceBufferPercent: offer.secondLienBalanceBufferPercent,
        refiLenderName: offer.refiLenderName,
        segments: [],
    };
}
function makeSegmentFromOffer(offer) {
    return {
        segmentKey: buildSliderBandSegmentKey(offer.identifier, offer.minLineSizeCents),
        offerIdentifier: offer.identifier,
        uwSpecId: offer.uwSpecId,
        minLineSizeCents: offer.minLineSizeCents,
        maxLineSizeCents: offer.maxLineSizeCents,
        fixedTermApr: offer.fixedTermApr,
        pricing: offer.pricing,
        recordingTaxFree: offer.recordingTaxFree,
        refiLiensDetails: offer.refiLiensDetails,
        fixedTermMonths: offer.fixedTermMonths,
    };
}
function sameRefiPositions(left, right) {
    if (left.length !== right.length) {
        return false;
    }
    const sortedLeft = [...left].sort((a, b) => a - b);
    const sortedRight = [...right].sort((a, b) => a - b);
    return sortedLeft.every((position, index) => position === sortedRight[index]);
}
function findCompatibleGroup(groups, offer) {
    return groups.find((group) => group.fixedTermMonths === offer.fixedTermMonths && valuesMatch(group.firstDrawFeeDecimal, offer.firstDrawFeeDecimal) && sameRefiPositions(group.refiPositions, offer.refiPositions));
}
function validateSegments(group, drops) {
    const sortedSegments = group.segments.slice().sort((a, b) => a.minLineSizeCents - b.minLineSizeCents || b.maxLineSizeCents - a.maxLineSizeCents || a.segmentKey.localeCompare(b.segmentKey));
    const keptSegments = [];
    for (const segment of sortedSegments) {
        if (segment.minLineSizeCents > segment.maxLineSizeCents) {
            drops?.push({
                identifier: segment.offerIdentifier,
                error: `segment ${segment.segmentKey} has minLineSizeCents=${segment.minLineSizeCents} greater than maxLineSizeCents=${segment.maxLineSizeCents}`,
            });
            continue;
        }
        const previousSegment = keptSegments[keptSegments.length - 1];
        if (previousSegment && segment.minLineSizeCents <= previousSegment.maxLineSizeCents) {
            drops?.push({
                identifier: segment.offerIdentifier,
                error: `segment ${segment.segmentKey} overlaps segment ${previousSegment.segmentKey} in ${group.groupKey}`,
            });
            continue;
        }
        keptSegments.push(segment);
    }
    return keptSegments;
}
/**
 * Adapter from flat SliderOfferConstants[] into grouped slider tracks.
 * Legacy offers contribute one segment; banded offers contribute one per pricing band.
 */
function buildSliderTrackGroups(sliderOfferConstants, drops) {
    const groups = [];
    for (const offer of sliderOfferConstants) {
        let group = findCompatibleGroup(groups, offer);
        if (!group) {
            group = makeGroupFromOffer(offer);
            groups.push(group);
        }
        const mismatch = getSharedParamMismatch(group, offer);
        if (mismatch) {
            drops?.push({ identifier: offer.identifier, error: mismatch });
            continue;
        }
        group.segments.push(makeSegmentFromOffer(offer));
    }
    return groups.flatMap((group) => {
        const segments = validateSegments(group, drops);
        if (segments.length === 0) {
            return [];
        }
        return [{ ...group, segments }];
    });
}
function getGroupRefiAwareMinLineSizeCents(group) {
    return (0, offerDetails_1.getRefiAwareMinLineSizeCents)(group.refiPayoffCents, group.priorLienPayoffFee, group.secondLienBalanceBufferPercent);
}
function getEffectiveSegmentMinLineSizeCents(group, segment) {
    return Math.max(segment.minLineSizeCents, getGroupRefiAwareMinLineSizeCents(group));
}
// The slider axis is net cash the applicant receives, so both directions deduct the whole upfront
// fee via oneTimeFeeDecimal.
function getSegmentRecordingTaxFixedCents(group, segment) {
    if (segment.recordingTaxFree) {
        return 0;
    }
    return group.recordingTaxFixedCents;
}
function getSegmentRecordingTaxVariableDecimal(group, segment) {
    if (segment.recordingTaxFree) {
        return 0;
    }
    return group.recordingTaxVariableDecimal;
}
function lineSizeToGroupCashAndPayoffCents(group, segment, lineSizeCents) {
    return (0, math_1.lineSizeToCashAndPayoffCents)(lineSizeCents, group.oneTimeFeeDecimal, getSegmentRecordingTaxFixedCents(group, segment), getSegmentRecordingTaxVariableDecimal(group, segment));
}
function cashAndPayoffToGroupLineSizeCents(group, segment, cashAndPayoffCents) {
    return (0, math_1.cashAndPayoffToLineSizeCents)(cashAndPayoffCents, group.oneTimeFeeDecimal, getSegmentRecordingTaxFixedCents(group, segment), getSegmentRecordingTaxVariableDecimal(group, segment));
}
function computeSliderTrackGroupCashBounds(groups, payoffCents, roundRefiAdditionalFloor = false) {
    let minCashAndPayoffCents = Infinity;
    let maxCashAndPayoffCents = -Infinity;
    for (const group of groups) {
        for (const segment of group.segments) {
            const minBound = (0, math_1.minimumCashAndPayoffForLineSizeCents)(segment.minLineSizeCents, group.oneTimeFeeDecimal, getSegmentRecordingTaxFixedCents(group, segment), getSegmentRecordingTaxVariableDecimal(group, segment));
            const maxBound = lineSizeToGroupCashAndPayoffCents(group, segment, segment.maxLineSizeCents);
            if (minBound < minCashAndPayoffCents) {
                minCashAndPayoffCents = minBound;
            }
            if (maxBound > maxCashAndPayoffCents) {
                maxCashAndPayoffCents = maxBound;
            }
        }
    }
    if (minCashAndPayoffCents === Infinity || maxCashAndPayoffCents === -Infinity) {
        return null;
    }
    minCashAndPayoffCents = Math.max(0, minCashAndPayoffCents);
    // Mirror computeSliderBounds: round the net cash above the committed payoff to the nearest
    // $1,000, capped at the ceiling so a refi near the max line still renders.
    if (roundRefiAdditionalFloor) {
        minCashAndPayoffCents = Math.min((0, offerDetails_1.roundCashAdditionalUpToNearestThousand)(minCashAndPayoffCents, payoffCents), maxCashAndPayoffCents);
    }
    return {
        minCashAndPayoffCents,
        maxCashAndPayoffCents,
        selectableMinCents: Math.max(payoffCents, minCashAndPayoffCents),
    };
}
function mergeAllowedCashSegments(segments) {
    const sortedSegments = segments.slice().sort((a, b) => a.startCents - b.startCents || a.endCents - b.endCents);
    const mergedSegments = [];
    for (const segment of sortedSegments) {
        const previousSegment = mergedSegments[mergedSegments.length - 1];
        if (!previousSegment) {
            mergedSegments.push(segment);
            continue;
        }
        if (segment.startCents - previousSegment.endCents <= 1) {
            mergedSegments[mergedSegments.length - 1] = {
                startCents: previousSegment.startCents,
                endCents: Math.max(previousSegment.endCents, segment.endCents),
            };
            continue;
        }
        mergedSegments.push(segment);
    }
    return mergedSegments;
}
/**
 * Compute selectable cash + payoff intervals from grouped line-size segments.
 *
 * The returned intervals are trimmed to the same left floor as the legacy
 * slider: max(payoffCents, minCashAndPayoffCents, refi-aware minimum). Interior
 * gaps remain non-selectable unless they have no integer cash-axis value inside
 * them. This preserves the invariant that every value returned by
 * clampAndSnapToAllowedSegments resolves to at least one active segment.
 */
function computeAllowedCashSegments(groups, payoffCents, roundRefiAdditionalFloor = false) {
    const bounds = computeSliderTrackGroupCashBounds(groups, payoffCents, roundRefiAdditionalFloor);
    if (!bounds) {
        return [];
    }
    const allowedSegments = [];
    for (const group of groups) {
        for (const segment of group.segments) {
            const effectiveMinLineSizeCents = getEffectiveSegmentMinLineSizeCents(group, segment);
            const startCents = Math.max(bounds.selectableMinCents, (0, math_1.minimumCashAndPayoffForLineSizeCents)(effectiveMinLineSizeCents, group.oneTimeFeeDecimal, getSegmentRecordingTaxFixedCents(group, segment), getSegmentRecordingTaxVariableDecimal(group, segment)));
            const endCents = lineSizeToGroupCashAndPayoffCents(group, segment, segment.maxLineSizeCents);
            if (startCents <= endCents) {
                allowedSegments.push({ startCents, endCents });
            }
        }
    }
    return mergeAllowedCashSegments(allowedSegments);
}
/**
 * Partition cash + payoff intervals while retaining every offer segment active across each interval.
 * Unlike computeAllowedCashSegments, this intentionally preserves provenance and does not merge ranges.
 */
function computeOfferRanges(groups, payoffCents) {
    const segments = groups.flatMap((group) => group.segments.map((segment) => ({
        segment,
        min: (0, math_1.minimumCashAndPayoffForLineSizeCents)(getEffectiveSegmentMinLineSizeCents(group, segment), group.oneTimeFeeDecimal, getSegmentRecordingTaxFixedCents(group, segment), getSegmentRecordingTaxVariableDecimal(group, segment)),
        max: lineSizeToGroupCashAndPayoffCents(group, segment, segment.maxLineSizeCents),
    })));
    const boundaries = [...new Set(segments.flatMap((segment) => [segment.min, segment.max + 1]))].sort((left, right) => left - right);
    const ranges = [];
    for (let index = 0; index < boundaries.length - 1; index += 1) {
        const start = boundaries[index];
        const end = boundaries[index + 1] - 1;
        const minCashAndPayoffCents = Math.max(start, payoffCents);
        if (minCashAndPayoffCents > end) {
            continue;
        }
        const activeSegments = segments.filter((candidate) => candidate.min <= start && candidate.max >= end).map(({ segment }) => segment);
        if (activeSegments.length > 0) {
            ranges.push({ minCashAndPayoffCents, maxCashAndPayoffCents: end, segments: activeSegments });
        }
    }
    return ranges;
}
/**
 * The group's APR bands on the cash + payoff axis, in slider order.
 *
 * Mirrors resolveActiveSegment: where two segments' cash intervals overlap through rounding, the
 * earlier segment wins, so each band starts one cent after the previous band ends. Consecutive
 * segments with the same APR merge even across a small gap (base and mega-line segments sit a
 * dollar of line apart), so every boundary between two bands is a rate change.
 */
function computeGroupAprBands(group) {
    const bands = [];
    for (const segment of group.segments) {
        const segmentStartCents = (0, math_1.minimumCashAndPayoffForLineSizeCents)(getEffectiveSegmentMinLineSizeCents(group, segment), group.oneTimeFeeDecimal, getSegmentRecordingTaxFixedCents(group, segment), getSegmentRecordingTaxVariableDecimal(group, segment));
        const endCents = lineSizeToGroupCashAndPayoffCents(group, segment, segment.maxLineSizeCents);
        const previousBand = bands[bands.length - 1];
        const startCents = previousBand ? Math.max(segmentStartCents, previousBand.endCents + 1) : segmentStartCents;
        if (endCents < startCents) {
            continue;
        }
        if (previousBand !== undefined && valuesMatch(previousBand.fixedTermApr, segment.fixedTermApr)) {
            previousBand.endCents = endCents;
            continue;
        }
        bands.push({ startCents, endCents, fixedTermApr: segment.fixedTermApr });
    }
    return bands;
}
/**
 * Clamp a cash + payoff value to the nearest selectable segment.
 *
 * Values inside a gap snap to the nearest segment edge, with exact ties going
 * to the lower edge. Values already inside a segment pass through except near
 * that segment's right edge, where they snap to the exact segment end so a
 * non-step-aligned maximum remains reachable.
 */
function clampAndSnapToAllowedSegments(valueCents, allowedSegments, stepCents = sliderBounds_1.DEFAULT_SLIDER_STEP_CENTS) {
    if (allowedSegments.length === 0) {
        return valueCents;
    }
    const firstSegment = allowedSegments[0];
    if (valueCents < firstSegment.startCents) {
        return firstSegment.startCents;
    }
    const lastSegment = allowedSegments[allowedSegments.length - 1];
    if (valueCents > lastSegment.endCents) {
        return lastSegment.endCents;
    }
    for (let index = 0; index < allowedSegments.length; index += 1) {
        const segment = allowedSegments[index];
        if (valueCents >= segment.startCents && valueCents <= segment.endCents) {
            return valueCents > segment.endCents - stepCents ? segment.endCents : valueCents;
        }
        const nextSegment = allowedSegments[index + 1];
        if (nextSegment && valueCents > segment.endCents && valueCents < nextSegment.startCents) {
            const distanceToLowerEdge = valueCents - segment.endCents;
            const distanceToUpperEdge = nextSegment.startCents - valueCents;
            return distanceToLowerEdge <= distanceToUpperEdge ? segment.endCents : nextSegment.startCents;
        }
    }
    return valueCents;
}
/**
 * Resolve the segment active at a slider position.
 *
 * `cashAndPayoffCents` is on the cash + payoff slider axis and must include the
 * same refi payoff and selected debt-swap balances used to build the track
 * config. Passing cash-only cents silently under-resolves the line size.
 */
function resolveActiveSegment(group, cashAndPayoffCents) {
    return (group.segments.find((segment) => {
        const desiredLineSizeCents = cashAndPayoffToGroupLineSizeCents(group, segment, cashAndPayoffCents);
        const effectiveMinLineSizeCents = getEffectiveSegmentMinLineSizeCents(group, segment);
        return desiredLineSizeCents >= effectiveMinLineSizeCents && desiredLineSizeCents <= segment.maxLineSizeCents;
    }) ?? null);
}
/**
 * Pick the best eligible group at a slider position.
 *
 * `cashAndPayoffCents` is on the cash + payoff slider axis and must include the
 * same refi payoff and selected debt-swap balances used to build the track
 * config. Passing cash-only cents silently under-resolves the line size.
 */
function pickBestEligibleGroupKey(groups, cashAndPayoffCents, currentGroupKey) {
    const currentStillEligible = groups.find((group) => group.groupKey === currentGroupKey && resolveActiveSegment(group, cashAndPayoffCents) !== null);
    if (currentStillEligible) {
        return currentGroupKey;
    }
    const bestEligible = groups.filter((group) => resolveActiveSegment(group, cashAndPayoffCents) !== null).sort((a, b) => b.fixedTermMonths - a.fixedTermMonths)[0];
    return bestEligible?.groupKey ?? null;
}
function getActiveBandSegment(group, activeSegment) {
    if (!activeSegment) {
        return null;
    }
    return {
        ...activeSegment,
        effectiveMinLineSizeCents: getEffectiveSegmentMinLineSizeCents(group, activeSegment),
    };
}
/**
 * Compute group details at a slider position.
 *
 * `cashAndPayoffCents` is on the cash + payoff slider axis and must include the
 * same refi payoff and selected debt-swap balances used to build the track
 * config. Passing cash-only cents silently under-resolves the line size.
 *
 * `debtSwapTradelines` must be the selected subset, not the full eligible list.
 * It is used only to compute the balance-transfer fee; the debt payoff amount
 * must already be included in `cashAndPayoffCents`.
 */
function getSliderGroupDetails(cashAndPayoffCents, debtSwapTradelines, groups) {
    const btPayoffCents = debtSwapTradelines.reduce((sum, tradeline) => sum + tradeline.balanceCents, 0);
    return groups.map((group) => {
        const activeSegment = resolveActiveSegment(group, cashAndPayoffCents);
        const desiredLineSizeCents = activeSegment
            ? cashAndPayoffToGroupLineSizeCents(group, activeSegment, cashAndPayoffCents)
            : (0, math_1.cashAndPayoffToLineSizeCents)(cashAndPayoffCents, group.oneTimeFeeDecimal, group.recordingTaxFixedCents, group.recordingTaxVariableDecimal);
        const recordingTaxFixedCents = activeSegment ? getSegmentRecordingTaxFixedCents(group, activeSegment) : group.recordingTaxFixedCents;
        const recordingTaxVariableDecimal = activeSegment ? getSegmentRecordingTaxVariableDecimal(group, activeSegment) : group.recordingTaxVariableDecimal;
        const recordingFee = (0, offerDetails_1.computeRecordingFee)(desiredLineSizeCents, recordingTaxFixedCents, recordingTaxVariableDecimal);
        return {
            constants: {
                groupKey: group.groupKey,
                fixedTermMonths: group.fixedTermMonths,
                firstDrawFeeDecimal: group.firstDrawFeeDecimal,
                refiPayoffCents: group.refiPayoffCents,
                refiLenderName: group.refiLenderName,
            },
            computed: {
                isEligible: activeSegment !== null,
                activeSegment: getActiveBandSegment(group, activeSegment),
                desiredLineSizeCents,
                oneTimeFee: (0, offerDetails_1.computeOneTimeFee)(desiredLineSizeCents, group.oneTimeFeeDecimal),
                balanceTransferFee: (0, offerDetails_1.computeBalanceTransferFee)(btPayoffCents, group.balanceTransferFeePercent),
                refiFee: (0, offerDetails_1.computeRefiFee)(group.refiPayoffCents, group.priorLienPayoffFee),
                recordingFee,
            },
        };
    });
}
function getFallbackAllowedCashSegments(config) {
    const bounds = (0, sliderBounds_1.computeSliderTrackBounds)(config);
    if (bounds.selectableMinCents > bounds.maxCents) {
        return [];
    }
    return [{ startCents: bounds.selectableMinCents, endCents: bounds.maxCents }];
}
function buildTrackConfigFromLegacyConfig(config) {
    const groups = buildSliderTrackGroups(config.sliderOfferConstants);
    const allowedCashSegments = computeAllowedCashSegments(groups, config.payoffCents);
    if (allowedCashSegments.length > 0) {
        return {
            groups,
            allowedCashSegments,
            payoffCents: config.payoffCents,
            refiPayoffCents: config.refiPayoffCents,
            debtSwapPayoffCents: config.debtSwapPayoffCents,
            minCashAndPayoffCents: config.minCashAndPayoffCents,
            maxCashAndPayoffCents: config.maxCashAndPayoffCents,
        };
    }
    return {
        groups,
        allowedCashSegments: getFallbackAllowedCashSegments(config),
        payoffCents: config.payoffCents,
        refiPayoffCents: config.refiPayoffCents,
        debtSwapPayoffCents: config.debtSwapPayoffCents,
        minCashAndPayoffCents: config.minCashAndPayoffCents,
        maxCashAndPayoffCents: config.maxCashAndPayoffCents,
    };
}
function computeTrackBoundsFromAllowedSegments(trackConfig) {
    const firstAllowedSegment = trackConfig.allowedCashSegments[0];
    const lastAllowedSegment = trackConfig.allowedCashSegments[trackConfig.allowedCashSegments.length - 1];
    const selectableMinCents = firstAllowedSegment?.startCents ?? Math.max(trackConfig.payoffCents, trackConfig.minCashAndPayoffCents);
    const maxCents = lastAllowedSegment?.endCents ?? trackConfig.maxCashAndPayoffCents;
    let visualMinCents = selectableMinCents;
    if (trackConfig.payoffCents > 0) {
        visualMinCents = 0;
    }
    return {
        visualMinCents,
        payoffEndsAtCents: trackConfig.payoffCents,
        selectableMinCents,
        maxCents,
    };
}
function isBuildSliderTrackConfigFailure(result) {
    return 'error' in result;
}
/**
 * Build the grouped slider configuration without changing the legacy
 * FrontendSliderConfig builder. This is the parallel entry point for clients
 * that can render gap-aware track groups.
 */
function buildSliderTrackConfig(underwritingResult, selectedDebtSwapTradelines, uwMetadataConstants, selectedFirstDrawFee, options = {}) {
    const totalOffers = underwritingResult.preQualOffers?.length ?? 0;
    const preQualOffers = (0, offerFilter_1.filterOffersByFirstDrawFee)(underwritingResult.preQualOffers ?? [], selectedFirstDrawFee);
    const drops = [];
    const fail = (error) => ({
        error,
        totalOffers,
        offersAfterFeeFilter: preQualOffers.length,
        drops,
    });
    const sliderOfferConstants = (0, configBuilder_1.extractSliderOfferConstants)(preQualOffers, uwMetadataConstants, drops);
    if (sliderOfferConstants.length === 0) {
        return fail('all offers dropped by extractSliderOfferConstants');
    }
    const groups = buildSliderTrackGroups(sliderOfferConstants, drops);
    if (groups.length === 0) {
        return fail('all offers dropped by buildSliderTrackGroups');
    }
    const selectedDebtSwapPayoffCents = selectedDebtSwapTradelines.reduce((sum, tradeline) => sum + tradeline.balanceCents, 0);
    const fundableGroups = groups.flatMap((group) => {
        const maxLineSizeCents = Math.max(...group.segments.map((segment) => segment.maxLineSizeCents));
        const feasibility = (0, offerDetails_1.getSliderOfferPayoffFeasibility)({ ...group, maxLineSizeCents }, selectedDebtSwapPayoffCents, uwMetadataConstants.debtSwapLineSizeBufferDecimal, options.roundRefiAdditionalFloor);
        if (!feasibility.isFeasible) {
            return [];
        }
        const segments = group.segments
            .filter((segment) => segment.maxLineSizeCents >= feasibility.requiredGrossLineSizeCents)
            .map((segment) => ({ ...segment, minLineSizeCents: Math.max(segment.minLineSizeCents, feasibility.requiredGrossLineSizeCents) }));
        return segments.length > 0 ? [{ ...group, segments }] : [];
    });
    if (fundableGroups.length === 0) {
        return fail('no track can fund committed payoffs');
    }
    const refiPayoffCents = Math.max(0, ...fundableGroups.map((group) => group.refiPayoffCents));
    const payoffCents = refiPayoffCents + selectedDebtSwapPayoffCents;
    const bounds = computeSliderTrackGroupCashBounds(fundableGroups, payoffCents, options.roundRefiAdditionalFloor);
    if (!bounds) {
        return fail('bounds never set for slider track groups');
    }
    const allowedCashSegments = computeAllowedCashSegments(fundableGroups, payoffCents, options.roundRefiAdditionalFloor);
    if (allowedCashSegments.length === 0) {
        return fail('no allowed cash segments');
    }
    return {
        groups: fundableGroups,
        allowedCashSegments,
        payoffCents,
        refiPayoffCents,
        debtSwapPayoffCents: selectedDebtSwapPayoffCents,
        minCashAndPayoffCents: bounds.minCashAndPayoffCents,
        maxCashAndPayoffCents: bounds.maxCashAndPayoffCents,
    };
}
